import React, { useCallback } from 'react'

import { ActionIcon, Text, Tooltip } from '@mantine/core'
import { CircularProgress, Grid } from '@mui/material'
import {
  IconBrandGoogleDrive,
  IconClick,
  // IconCloudDownload,
  IconServerSpark,
} from '@tabler/icons-react'
import { useGetList, useTranslate } from 'react-admin'

import { Link } from 'react-router-dom'
import {
  ArtworkApiDTO,
  GetApiMediaUploaderProjectsProjectIdOrGuidFilesIdOrGuid200,
} from '../../codegen/generated/Api.schemas'
import { getApiMediaUploaderProjectsProjectIdOrGuidFilesIdOrGuidUploadSignedUrl } from '../../codegen/generated/media-uploader-projects'
import { DestinationsHelper } from '../../helpers/Destinations.helper'
import { FileHelper } from '../../helpers/File.helper'
import { NumberFormatter } from '../../helpers/NumberFormatter.helper'
import { useUserStore } from '../../stores/useUserStore'
import { LXNoData } from '../LXNoData'
import { LXYoutubeIcon } from '../LXYoutubeIcon'
import { LXField } from './../LXField'

export interface ListMediaFilesProps {
  artworkId: ArtworkApiDTO['id']
}

export const ListMediaFiles: React.FC<ListMediaFilesProps> = ({ artworkId }) => {
  const translate = useTranslate()
  const userStore = useUserStore()

  const mediaFilesQuery = useGetList<GetApiMediaUploaderProjectsProjectIdOrGuidFilesIdOrGuid200>(
    'mediaUploaderFiles',
    {
      sort: { field: 'id', order: 'ASC' },
      pagination: { page: 1, perPage: 1000 },
      filter: {
        artworkId,
      },
    },
  )

  const downloadFile = useCallback(
    async (
      file?: GetApiMediaUploaderProjectsProjectIdOrGuidFilesIdOrGuid200,
    ) => {
      if (file) {
        const { id: fileId, mediaUploaderProjectId, localFileName } = file
        const fileExtension = (/[^./\\]*$/.exec(localFileName ?? '') || [''])[0].toLowerCase()
        const contentType = FileHelper.getMimeTypeFromExtension(fileExtension)

        const projectFilesSignedUrl =
          await getApiMediaUploaderProjectsProjectIdOrGuidFilesIdOrGuidUploadSignedUrl(
            mediaUploaderProjectId,
            fileId,
            { fileExtension, contentType },
          )

        FileHelper.downloadUrl(projectFilesSignedUrl.body?.signedUrl)
      }
    },
    [],
  )

  const Undefined = () => (
    <Text size="sm" fs="italic" lh="2.5" c="gray">
      {translate('_generics.undefined')}
    </Text>
  )

  return (
    <>
      <Grid container direction="row" spacing={1}>
        {mediaFilesQuery.isLoading ? (
          <div style={{ marginTop: '2rem' }}>
            <CircularProgress size="2rem" />
          </div>
        ) : (mediaFilesQuery?.data ?? []).length > 0 ? (
          (mediaFilesQuery?.data ?? []).map((mediaFile, index) => (
            <Grid container item direction="row" lg={12} spacing={2} key={mediaFile.id}>
              <LXField
                label={index === 0 ? translate('_generics.name') : undefined}
                size={3}
                labeled
              >
                <Text
                  mt={8}
                  style={{
                    wordBreak: 'break-all',
                  }}
                >
                  {mediaFile.fileDisplayName ?? mediaFile.localFileName ?? <Undefined />}
                </Text>
              </LXField>

              <LXField
                label={index === 0 ? translate('_generics.size') : undefined}
                size={1}
                labeled
              >
                {(mediaFile.metadata as any)?.size ? (
                  NumberFormatter.formatDigitalByte((mediaFile.metadata as any)?.size)
                ) : (
                  <Undefined />
                )}
              </LXField>
              <LXField
                label={index === 0 ? translate('pages.mediaFiles.diffusionZone') : undefined}
                size={1}
                labeled
              >
                {(mediaFile.metadata as any)?.diffusionZone ? (
                  <Text mt={8} style={{ wordBreak: 'break-word' }}>
                    {(mediaFile.metadata as any).diffusionZone}
                  </Text>
                ) : (
                  <Undefined />
                )}
              </LXField>
              {/*
              <LXField
                label={index === 0 ? translate('_generics.file') : undefined}
                size={1}
                labeled
              >
                <ActionIcon
                  variant="subtle"
                  onClick={() => downloadFile(mediaFile)}
                  disabled={
                    mediaFile.destinationColdStorage?.enabled === false &&
                    mediaFile.destinationColdStorage?.completed === false
                  }
                >
                  <IconCloudDownload size={20} />
                </ActionIcon>
              </LXField> */}
              <LXField
                label={index === 0 ? translate('pages.mediaFiles.drive') : undefined}
                size={1}
                labeled
              >
                {mediaFile.destinationDrive?.completed ? (
                  <Link
                    to={DestinationsHelper.getDriveURL(mediaFile.destinationDrive.driveFileId)}
                    target="_blank"
                  >
                    <ActionIcon variant="subtle">
                      <IconBrandGoogleDrive size={20} />
                    </ActionIcon>
                  </Link>
                ) : (
                  <ActionIcon variant="subtle" disabled>
                    <IconBrandGoogleDrive size={20} />
                  </ActionIcon>
                )}
              </LXField>
              <LXField
                label={index === 0 ? translate('pages.mediaFiles.youtubeFootprint') : undefined}
                size={2}
                labeled
              >
                {mediaFile.destinationYoutubeFootprint?.completed ? (
                  <Link
                    to={DestinationsHelper.getFootprintURL(
                      mediaFile.destinationYoutubeFootprint.youtubeAssetId,
                      mediaFile.destinationYoutubeFootprint.onBehalfOfContentOwner,
                    )}
                    target="_blank"
                    style={{
                      width: 'fit-content',
                    }}
                  >
                    <ActionIcon
                      variant="subtle"
                      size="lg"
                      aria-label={translate('pages.mediaFiles.youtubeFootprint')}
                    >
                      <LXYoutubeIcon />
                    </ActionIcon>
                  </Link>
                ) : (
                  <ActionIcon
                    variant="subtle"
                    size="lg"
                    disabled
                    aria-label={translate('pages.mediaFiles.youtubeFootprint')}
                  >
                    <LXYoutubeIcon />
                  </ActionIcon>
                )}
              </LXField>
              <LXField
                label={index === 0 ? translate('pages.mediaFiles.youtubeVideo') : undefined}
                size={1}
                labeled
              >
                {mediaFile.destinationYoutubeChannel?.completed ? (
                  <Link
                    to={DestinationsHelper.getYoutubeChannelURL(
                      mediaFile.destinationYoutubeChannel.youtubeVideoId,
                    )}
                    target="_blank"
                    style={{
                      width: 'fit-content',
                    }}
                  >
                    <ActionIcon
                      variant="subtle"
                      size="lg"
                      aria-label={translate('pages.mediaFiles.youtubeVideo')}
                    >
                      <LXYoutubeIcon />
                    </ActionIcon>
                  </Link>
                ) : (
                  <ActionIcon
                    variant="subtle"
                    size="lg"
                    disabled
                    aria-label={translate('pages.mediaFiles.youtubeVideo')}
                  >
                    <LXYoutubeIcon />
                  </ActionIcon>
                )}
              </LXField>
              <LXField
                label={index === 0 ? translate('pages.mediaFiles.clickupLink') : undefined}
                size={1}
                labeled
              >
                {mediaFile.destinationClickup?.completed &&
                mediaFile.destinationYoutubeChannel?.completed &&
                mediaFile.destinationYoutubeChannel.clickupTaskId ? (
                  <Link
                    to={DestinationsHelper.getClickUpPath(
                      mediaFile.destinationYoutubeChannel.clickupTaskId,
                    )}
                    target="_blank"
                    style={{
                      width: 'fit-content',
                    }}
                  >
                    <ActionIcon variant="subtle">
                      <IconClick size={20} />
                    </ActionIcon>
                  </Link>
                ) : (
                  <ActionIcon variant="subtle" disabled>
                    <IconClick size={20} />
                  </ActionIcon>
                )}
              </LXField>
              <LXField
                label={index === 0 ? translate('pages.mediaFiles.coldStorage') : undefined}
                size={2}
                labeled
              >
                <Tooltip
                  label={`⚠ ${translate('pages.users.lackingPermission')} → ${translate(
                    'pages.users.permissionsList.media_uploader.cold_storage_access',
                  )}`}
                  hidden={userStore.isAuthorised('media_uploader.cold_storage_access')}
                >
                  {mediaFile.destinationColdStorage?.completed &&
                  mediaFile.coldStorageSignedUrl &&
                  userStore.isAuthorised('media_uploader.cold_storage_access') ? (
                    <Link
                      to={mediaFile.coldStorageSignedUrl}
                      target="_blank"
                      style={{
                        width: 'fit-content',
                      }}
                    >
                      <ActionIcon variant="subtle">
                        <IconServerSpark size={20} />
                      </ActionIcon>
                    </Link>
                  ) : (
                    <ActionIcon variant="subtle" disabled>
                      <IconServerSpark size={20} />
                    </ActionIcon>
                  )}
                </Tooltip>
              </LXField>
            </Grid>
          ))
        ) : (
          <LXNoData />
        )}
      </Grid>
    </>
  )
}
